import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Briefcase, Users, Calendar, Trash2, Edit, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import ProjectDetailModal from './ProjectDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  active: { label: 'Aktiv', color: 'bg-emerald-100 text-emerald-800' },
  on_hold: { label: 'Gözləyir', color: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Tamamlandı', color: 'bg-blue-100 text-blue-800' },
  archived: { label: 'Arxiv', color: 'bg-zinc-100 text-zinc-600' },
};

const PRIORITY_LABELS = {
  low: { label: 'Aşağı', color: 'text-zinc-600' },
  medium: { label: 'Orta', color: 'text-amber-600' },
  high: { label: 'Yüksək', color: 'text-red-600' },
};

const COLORS = ['#9333ea', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1'];

export default function ProjectsPanel({ user }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('active');
  const [showCreate, setShowCreate] = useState(false);
  const [detailProject, setDetailProject] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    priority: 'medium',
    color: '#9333ea',
    start_date: '',
    due_date: '',
  });

  const canCreate = user?.role === 'admin' || user?.is_superadmin || user?.role === 'superadmin';

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const status = tab === 'all' ? null : tab;
      const url = status ? `${API}/projects?status=${status}` : `${API}/projects`;
      const res = await axios.get(url, { headers });
      setProjects(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Layihələr yüklənmədi');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Ad sahəsi məcburidir');
      return;
    }
    try {
      await axios.post(`${API}/projects`, form, { headers });
      toast.success('Layihə yaradıldı');
      setShowCreate(false);
      setForm({
        name: '',
        description: '',
        priority: 'medium',
        color: '#9333ea',
        start_date: '',
        due_date: '',
      });
      fetchProjects();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Layihə yaradılmadı');
    }
  };

  const handleArchive = async (id, name) => {
    if (!window.confirm(`"${name}" layihəsini arxivləşdirmək istədiyinizə əminsiniz?`)) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { headers });
      toast.success('Arxivləşdirildi');
      fetchProjects();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Arxivləşdirilmədi');
    }
  };

  return (
    <div className="space-y-4" data-testid="projects-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Briefcase className="h-6 w-6 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold">Layihə İdarəetməsi</h2>
            <p className="text-sm text-zinc-600">
              Uzunmüddətli layihələrin idarəsi (Kanban, Gantt, Xəritə)
            </p>
          </div>
        </div>
        {canCreate && (
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="create-project-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Yeni Layihə
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active">Aktiv</TabsTrigger>
          <TabsTrigger value="on_hold" data-testid="tab-on_hold">Gözləyir</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Tamamlandı</TabsTrigger>
          <TabsTrigger value="archived" data-testid="tab-archived">Arxiv</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading && <div className="text-sm text-zinc-500 py-4">Yüklənir...</div>}

      {!loading && projects.length === 0 && (
        <div className="border border-dashed border-zinc-300 rounded-lg py-12 text-center text-zinc-500">
          <Briefcase className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
          <div className="text-sm">
            Bu statusda layihə yoxdur
            {canCreate && (
              <div className="mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreate(true)}
                >
                  <Plus className="h-4 w-4 mr-1" /> İlk layihəni yarat
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((p) => (
          <Card
            key={p.id}
            className="hover:shadow-md transition-shadow border-l-4 cursor-pointer"
            style={{ borderLeftColor: p.color || '#9333ea' }}
            onClick={() => setDetailProject(p)}
            data-testid={`project-card-${p.id}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base font-semibold leading-snug">
                  {p.name}
                </CardTitle>
                {canCreate && p.status !== 'archived' && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 -mr-2 -mt-1 text-zinc-400 hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArchive(p.id, p.name);
                    }}
                    title="Arxivləşdir"
                    data-testid={`archive-${p.id}`}
                  >
                    <Archive className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-2">
                <Badge className={STATUS_LABELS[p.status]?.color || 'bg-zinc-100'}>
                  {STATUS_LABELS[p.status]?.label || p.status}
                </Badge>
                <span className={`text-xs font-medium ${PRIORITY_LABELS[p.priority]?.color}`}>
                  ● {PRIORITY_LABELS[p.priority]?.label || p.priority}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {p.description && (
                <p className="text-xs text-zinc-600 line-clamp-2">{p.description}</p>
              )}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-zinc-600">
                  <span>İrəliləyiş</span>
                  <span className="font-semibold">{p.progress || 0}%</span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${p.progress || 0}%`,
                      backgroundColor: p.color || '#9333ea',
                    }}
                  />
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {p.task_done || 0} / {p.task_count || 0} tapşırıq
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-zinc-500 pt-2 border-t">
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {p.member_count || 0} üzv
                </span>
                {p.due_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {new Date(p.due_date).toLocaleDateString('az-AZ')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-project-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Layihə</DialogTitle>
            <DialogDescription className="sr-only">
              Yeni layihənin yaradılması üçün ad, açıqlama, prioritet və tarixlər.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Ad *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Layihənin adı"
                data-testid="project-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Açıqlama</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Layihə haqqında..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Prioritet</label>
                <Select
                  value={form.priority}
                  onValueChange={(v) => setForm({ ...form, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Aşağı</SelectItem>
                    <SelectItem value="medium">Orta</SelectItem>
                    <SelectItem value="high">Yüksək</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Rəng</label>
                <div className="flex gap-1 mt-1.5">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`h-7 w-7 rounded-full transition-transform ${
                        form.color === c ? 'ring-2 ring-offset-2 ring-zinc-800 scale-110' : ''
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Başlanma tarixi</label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Bitmə tarixi</label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Ləğv et
            </Button>
            <Button
              onClick={handleCreate}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="submit-project-btn"
            >
              Yarat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <ProjectDetailModal
        open={!!detailProject}
        project={detailProject}
        user={user}
        onClose={() => setDetailProject(null)}
        onChange={fetchProjects}
      />
    </div>
  );
}
